/**
 * CopyableId — small atom rendering a label + value with a copy-to-clipboard
 * action. Used in the hover header so a debugger can grab the item id with
 * one click instead of squinting at the value.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { Copy } from 'lucide-react';
import { FC, MouseEvent, useCallback } from 'react';

import { cn } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

export interface CopyableIdProps {
  label: string;
  value: string;
  className?: string;
}

const CopyableId: FC<CopyableIdProps> = ({ label, value, className }) => {
  const toast = useToast();

  const handleClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(value);
        toast.success('Copied', `${label} copied to clipboard.`);
      } catch {
        toast.error('Copy failed', 'Clipboard access was rejected.');
      }
    },
    [label, value, toast],
  );

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 tabular-nums text-muted-foreground',
        className,
      )}
    >
      <span>{label}</span>
      <span className="text-foreground">{value}</span>
      <button
        type="button"
        onClick={handleClick}
        aria-label={`Copy ${label}`}
        className="inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      >
        <Copy className="h-3 w-3" aria-hidden="true" />
      </button>
    </span>
  );
};

export default CopyableId;
